import { useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ProposalCard } from './ProposalCard';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/solid';
// XMarkIcon used for offering delete buttons
import type { Phase1Proposal } from '@/lib/services/agent-wizard';

const COMPANY_SIZE_RANGES = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1001-5000', '5001+'] as const;

function matchCompanySize(value: string): string {
  // If it's already a valid range, return it
  if (COMPANY_SIZE_RANGES.includes(value as any)) return value;

  // Extract numbers from the string
  const numbers = value.match(/\d+/g)?.map(Number) || [];
  if (numbers.length === 0) return '';

  // Use the midpoint if range, or the single number
  const target = numbers.length >= 2 ? (numbers[0] + numbers[1]) / 2 : numbers[0];

  // Find the best matching range
  const ranges = [
    { value: '1-10', mid: 5 },
    { value: '11-50', mid: 30 },
    { value: '51-200', mid: 125 },
    { value: '201-500', mid: 350 },
    { value: '501-1000', mid: 750 },
    { value: '1001-5000', mid: 3000 },
    { value: '5001+', mid: 10000 },
  ];

  let closest = ranges[0];
  let minDiff = Math.abs(target - closest.mid);
  for (const r of ranges) {
    const diff = Math.abs(target - r.mid);
    if (diff < minDiff) {
      minDiff = diff;
      closest = r;
    }
  }
  return closest.value;
}

interface Phase1Props {
  proposal: Phase1Proposal;
  onChange: (updated: Phase1Proposal) => void;
}

export function Phase1BusinessAnalysis({ proposal, onChange }: Phase1Props) {
  const updateField = <K extends keyof Phase1Proposal>(key: K, value: Phase1Proposal[K]) => {
    onChange({ ...proposal, [key]: value });
  };

  const updateIcpField = (key: string, value: string) => {
    onChange({
      ...proposal,
      icp: { ...proposal.icp, [key]: value },
    });
  };

  const addOffering = () => {
    onChange({
      ...proposal,
      offerings: [...proposal.offerings, { name: '', description: '' }],
    });
  };

  const removeOffering = (index: number) => {
    onChange({
      ...proposal,
      offerings: proposal.offerings.filter((_, i) => i !== index),
    });
  };

  const updateOffering = (index: number, field: 'name' | 'description', value: string) => {
    const updated = [...proposal.offerings];
    updated[index] = { ...updated[index], [field]: value };
    onChange({ ...proposal, offerings: updated });
  };


  const autoResize = useCallback((el: HTMLTextAreaElement | null) => {
    if (el) {
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
    }
  }, []);

  return (
    <div className="space-y-6">
      <ProposalCard title="Business Summary">
        <Textarea
          ref={autoResize}
          value={proposal.businessSummary}
          onChange={(e) => { updateField('businessSummary', e.target.value); autoResize(e.target); }}
          className="min-h-[80px] !bg-input-bg resize-none overflow-hidden"
          placeholder="What does this business do?"
        />
      </ProposalCard>

      <ProposalCard title="Offerings">
        <div className="space-y-4">
          {proposal.offerings.map((offering, index) => (
            <div key={index} className="p-4 bg-surface-sunken rounded-xl space-y-3 relative group">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Offering {index + 1}</span>
                {proposal.offerings.length > 1 && (
                  <button
                    onClick={() => removeOffering(index)}
                    className="w-6 h-6 rounded-full bg-surface-track hover:bg-danger-surface-strong text-muted-foreground hover:text-danger-text transition-colors flex items-center justify-center cursor-pointer"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                )}
              </div>
              <Input
                value={offering.name}
                onChange={(e) => updateOffering(index, 'name', e.target.value)}
                placeholder="Offering name"
                inlineLabel="Name"
                className="w-full"
                style={{ paddingLeft: '6.4rem' }}
              />
              <Input
                value={offering.description}
                onChange={(e) => updateOffering(index, 'description', e.target.value)}
                placeholder="What problem does this solve for clients?"
                inlineLabel="Solves"
                className="w-full"
                style={{ paddingLeft: '6.4rem' }}
              />
            </div>
          ))}
          <Button
            variant="secondary"
            size="sm"
            onClick={addOffering}
            className="gap-1.5"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            Add offering
          </Button>
        </div>
      </ProposalCard>

      <ProposalCard title="Ideal Customer Profile">
        <div className="space-y-4">
          <Input
            value={proposal.icp.title}
            onChange={(e) => updateIcpField('title', e.target.value)}
            placeholder="e.g. Head of Engineering"
            inlineLabel="Job Title"
            className="w-full"
            style={{ paddingLeft: '7.33rem' }}
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              value={proposal.icp.seniority}
              onChange={(e) => updateIcpField('seniority', e.target.value)}
              placeholder="e.g. Director"
              inlineLabel="Seniority"
              className="w-full"
              style={{ paddingLeft: '7.33rem' }}
            />
            <Input
              value={proposal.icp.industry}
              onChange={(e) => updateIcpField('industry', e.target.value)}
              placeholder="e.g. Manufacturing"
              inlineLabel="Industry"
              className="w-full"
              style={{ paddingLeft: '7.33rem' }}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Select value={matchCompanySize(proposal.icp.companySize)} onValueChange={(v) => updateIcpField('companySize', v)}>
              <SelectTrigger className="h-12 rounded-full bg-input-bg border border-control-border shadow-lg relative" style={{ paddingLeft: '7.33rem', paddingRight: '1.33rem' }}>
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-normal pointer-events-none">Size</span>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1-10">1-10 employees</SelectItem>
                <SelectItem value="11-50">11-50 employees</SelectItem>
                <SelectItem value="51-200">51-200 employees</SelectItem>
                <SelectItem value="201-500">201-500 employees</SelectItem>
                <SelectItem value="501-1000">501-1,000 employees</SelectItem>
                <SelectItem value="1001-5000">1,001-5,000 employees</SelectItem>
                <SelectItem value="5001+">5,001+ employees</SelectItem>
              </SelectContent>
            </Select>
            <Input
              value={proposal.icp.geography}
              onChange={(e) => updateIcpField('geography', e.target.value)}
              placeholder="e.g. United Kingdom"
              inlineLabel="Region"
              className="w-full"
              style={{ paddingLeft: '7.33rem' }}
            />
          </div>

          {/* Additional details */}
          <div className="pt-3">
            <label className="text-xs font-medium text-muted-foreground mb-2 block">Additional Details</label>
            <Textarea
            ref={autoResize}
            value={(proposal.icp.customFilters || []).join('\n')}
            onChange={(e) => { onChange({
              ...proposal,
              icp: { ...proposal.icp, customFilters: e.target.value.split('\n').filter(Boolean) },
            }); autoResize(e.target); }}
            placeholder="Any additional details about your ideal customer — e.g. high energy consumption, multiple sites, recently funded..."
            className="min-h-[80px] !bg-input-bg resize-none overflow-hidden"
          />
          </div>
        </div>
      </ProposalCard>
    </div>
  );
}
